import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface InsightsSlide {
  key: string;
  kicker?: string;
  title: string;
  /** Contenido de la slide. Debe respetar el alto fijo del carrousel. */
  render: () => ReactNode;
}

interface Props {
  slides: InsightsSlide[];
  /** Alto fijo en CSS (px o tailwind). Default: '22rem' (~352px). */
  height?: string;
  /** Pestañas tipo pill arriba para navegar directo a una slide. Default true. */
  showTabs?: boolean;
  /** Cambiar slide automáticamente cada N ms (0 = off). Default: 0. */
  autoplayMs?: number;
}

/**
 * Carrousel horizontal con dots + flechas + tabs para agrupar insights
 * densos sin que el dashboard pase del viewport. Ver
 * feedback_page_height.md (regla de altura). Navegación:
 *  - Flechas ← → a los lados
 *  - Dots clickeables abajo
 *  - Tabs tipo pill arriba con el título de cada slide
 *  - Teclado: ← → cuando el carrousel tiene foco
 *  - Autoplay opcional (se pausa en hover / foco)
 */
export function InsightsCarousel({
  slides,
  height = '22rem',
  showTabs = true,
  autoplayMs = 0,
}: Props) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const total = slides.length;
  const canNav = total > 1;

  useEffect(() => {
    if (!autoplayMs || paused || !canNav) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), autoplayMs);
    return () => clearInterval(t);
  }, [autoplayMs, paused, canNav, total]);

  function go(delta: number) {
    setIdx((i) => (i + delta + total) % total);
  }

  function onKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!canNav) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
  }

  if (total === 0) return null;
  const current = slides[idx]!;

  return (
    <div
      ref={containerRef}
      className="card-premium overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKey}
      tabIndex={0}
    >
      {/* Header: tabs + flechas */}
      <div className="flex items-center justify-between gap-3 border-b border-surface-line px-4 pt-3">
        {showTabs ? (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 pb-3">
            {slides.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setIdx(i)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition',
                  i === idx
                    ? 'bg-blue-10 text-blue'
                    : 'text-neutral-muted hover:bg-surface-bg hover:text-neutral-ink',
                )}
              >
                {s.title}
              </button>
            ))}
          </div>
        ) : (
          <div className="min-w-0 flex-1 pb-3">
            {current.kicker && <p className="kicker">{current.kicker}</p>}
            <h2 className="mt-0.5 text-base font-extrabold tracking-tight">
              {current.title}
            </h2>
          </div>
        )}
        {canNav && (
          <div className="flex flex-none items-center gap-1 pb-2">
            <button
              type="button"
              onClick={() => go(-1)}
              className="grid h-7 w-7 place-items-center rounded-md text-neutral-muted transition hover:bg-blue-10 hover:text-blue"
              aria-label="Anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="grid h-7 w-7 place-items-center rounded-md text-neutral-muted transition hover:bg-blue-10 hover:text-blue"
              aria-label="Siguiente"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Slide viewport: transform horizontal con fade suave */}
      <div className="relative" style={{ height }}>
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{
            width: `${total * 100}%`,
            transform: `translateX(-${(idx * 100) / total}%)`,
          }}
        >
          {slides.map((s) => (
            <div
              key={s.key}
              className="flex-none overflow-y-auto p-4"
              style={{ width: `${100 / total}%`, height }}
              aria-hidden={s.key !== current.key}
            >
              {s.render()}
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      {canNav && (
        <div className="flex items-center justify-center gap-1.5 border-t border-surface-line/60 py-2">
          {slides.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setIdx(i)}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === idx
                  ? 'w-6 bg-blue'
                  : 'w-1.5 bg-neutral-muted/30 hover:bg-neutral-muted/60',
              )}
              aria-label={`Ir a ${s.title}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
